/* @flow */
import { DebugLog } from './debug-log';

export interface OgAdContext {
  ad_session_id?: string;
  ad_unit_config?: { asset_key?: string; ad_unit_id?: string };
  delivery_mode?: 'DIRECT' | 'INDIRECT' | 'HEADER_BIDDING';
  bidding_provider?: 'TAM' | 'GOOGLEOB' | 'PREBID' | 'PREBIDS2S';
  media_type?: 'image' | 'video';
  publisher_name?: string;
  ad_manager_click_url?: string;
  background_transparent?: boolean;
  ad_track_url?: string;
  format_size_response?: string;
  enable_supply_monitoring_events?: boolean;
  skoverlay?: { autoclick?: 0 | 1 };
  storekit?: { autoclick?: 0 | 1 };
}

export function ua() {
  let ua = window && navigator.userAgent.toLowerCase();
  if (window.OG_DEVICE_TYPE === 'desktop') {
    ua = 'desktop';
  }
  return ua || 'unknown';
}

// shouln't be used outside feture-enabling.js
export function isAndroid() {
  if (typeof window !== 'undefined') {
    return /android/i.test(ua());
  }

  return false;
}

// shouln't be used outside feature-enabling.js
export function isIos() {
  if (typeof window !== 'undefined') {
    return (
      /iphone|ipad|ipod/i.test(ua()) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  return false;
}

export function isInternal(name: string) {
  return name.toLowerCase() === 'ogury';
}

export function isWeb() {
  return Boolean(window && window.MRAID_ENV && window.MRAID_ENV.mwebFlag);
}

export function isMobile() {
  return isAndroid() || isIos();
}

export function isMobileWeb() {
  return isWeb() && isMobile();
}

// shouln't be used outside feture-enabling.js
export function getAndroidVersion() {
  const match = ua().match(/android\s?([0-9.]*)/);
  return match ? parseFloat(match[1]) : 0;
}

// shouln't be used outside feture-enabling.js
export function getIosVersion() {
  const match = ua().match(/os ((\d+_?){2,5})\s/);
  return match && match[1] ? parseFloat(match[1].replace(/_/g, '.')) : 0;
}

// shouln't be used outside feture-enabling.js
export function getSdkVersion() {
  return window.MRAID_ENV.sdkVersion;
}

// shouln't be used outside feture-enabling.js
export function isInternalSdk(): boolean {
  return Boolean(
    window.MRAID_ENV && window.MRAID_ENV.sdk?.toLowerCase() === 'presage'
  );
}

// shouln't be used outside feture-enabling.js
export function isExternalSdk(): boolean {
  return !isInternalSdk();
}

export function isChromeBrowser(userAgent = navigator.userAgent) {
  return /\b(?:crmo|crios|Chrome)\/([\w.]+)/i.test(userAgent);
}

// shouln't be used outside feture-enabling.js
export function getBrowserVersion() {
  return isAndroid() ? getChromiumVersion() : getSafariVersion();
}

// shouln't be used outside feture-enabling.js
export function getChromiumVersion() {
  const match = ua().match(/chrom(e|ium)\/([0-9]+)\./);
  return match ? parseInt(match[2], 10) : 0;
}

// shouln't be used outside feture-enabling.js
export function getSafariVersion() {
  const match = ua().match(/safari\/([0-9]+)\./);
  return match ? parseInt(match[1], 10) : 0;
}

export function versionIsAboveOrEqual(
  currentVersion: string,
  targetVersion: string
): boolean {
  const versionNumberRegex = /^\d+(\.\d+)*/;
  if (
    !targetVersion ||
    !currentVersion ||
    !targetVersion.match(versionNumberRegex) ||
    !currentVersion.match(versionNumberRegex)
  ) {
    DebugLog.log(
      `versionIsAboveOrEqual(targetVersion, currentVersion): expects targetVersion and currentVersion to be formated the following way x.x.x`
    );
    return false;
  }

  // Clean versions from specific suffixes (-B, -C, -baidu...)
  const cleanedCurrentVersion = currentVersion.split('-')[0];
  const cleanedTargetVersion = targetVersion.split('-')[0];

  const targetVersionTokens = cleanedTargetVersion.split('.');
  const currentVersionTokens = cleanedCurrentVersion.split('.');
  let longestLength =
    targetVersionTokens.length > currentVersionTokens.length
      ? targetVersionTokens.length
      : currentVersionTokens.length;
  for (let i = 0; i < longestLength; i++) {
    if (
      targetVersionTokens[i] &&
      currentVersionTokens[i] &&
      targetVersionTokens[i] != currentVersionTokens[i]
    ) {
      return (
        parseInt(targetVersionTokens[i]) <= parseInt(currentVersionTokens[i])
      );
    }
  }
  return true;
}

export function versionIsStrictlyAbove(
  currentVersion: string,
  targetVersion: string
) {
  return (
    currentVersion !== targetVersion &&
    versionIsAboveOrEqual(currentVersion, targetVersion)
  );
}

export function getOgAdContext(): OgAdContext {
  return window.OG_AD_CONTEXT || {};
}
